<script setup lang="ts">
//#region import
import { computed, ref } from 'vue';
import { cloneDeep } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import { AUTH_ADMIN_INFO } from '@/store/action_types/auth';
import { useTypedStore } from '@/store';
import { useApi } from '@/api';

import { type FormErrors, type EmitsToListeners, isErrorResponse } from '@/types/core';
import type { GetMessengersResponse } from '@/types/openapi-ts';
//#endregion

export interface  Emits {
  'close': [],
};

export interface EditProfileParams {
  listeners?: EmitsToListeners<Emits>
}

//#region emits
const emit = defineEmits<Emits>();
//#endregion

//#region const
const api = useApi();
const store = useTypedStore();
const { t } = useI18n();
//#endregion

//#region data
const isVisible = ref(false);
const errors = ref<FormErrors>({});
//@ts-expect-error инициализация без проверки типа
const profileInfo = ref<GetProfileResponse['payload']>({});
//#endregion

//#region computed
const adminInfo = computed(() => store.getters['auth/adminInfo']);
const timezones = computed(() => store.getters['misc/timezones']);
const messengers = computed<GetMessengersResponse['payload']>(() => store.getters['misc/apiMessengers']);

const messengerPlaceholder = computed(() => {
  if (profileInfo.value.messenger) {
    const placeholder = messengers.value.find(({id}) => id === profileInfo.value.messenger);
    return placeholder ? placeholder.example_placeholder : '';
  }
  return `${t('team_popup.messenger_id_placeholder')}`;
});
//#endregion

//#region methods
const open = () => {
  profileInfo.value = cloneDeep(adminInfo.value);
  isVisible.value = true;
};

const handleClose = () => {
  emit('close');
  isVisible.value = false;
  errors.value = {};
};
const handleSave = () => {
  errors.value = {};
  api.putProfileInfo(profileInfo.value)
    .then((response) => {
      store.commit(`auth/${AUTH_ADMIN_INFO}`, response.data.payload);
      handleClose();
    })
    .catch((error) => {
      if (isErrorResponse(error)) {
        errors.value = error.data.errors;
      }
    });
};
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="edit-profile-popup"
    :title="$t('team_popup.edit_user_title', { name: adminInfo.name })"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiInput
          v-model="profileInfo.name"
          :error="errors.name"
          :placeholder="$t(`team_popup.name_placeholder`)"
          :label="$t(`team_popup.name_label`)"
          size="big"
          autosize
        />
        <UiSelectElement
          v-model="profileInfo.timezone"
          :label="$t(`team_popup.timezone_label`)"
          :placeholder="$t('team_popup.timezone_placeholder')"
          :list="timezones"
          option-value-param="zone"
          size="big"
          filterable
        />
        <div class="t:flex t:space-x-16">
          <UiSelectElement
            v-model="profileInfo.messenger"
            :label="$t(`team_popup.messenger_label`)"
            :placeholder="$t('team_popup.messenger_placeholder')"
            :list="messengers"
            size="big"
            @change="profileInfo.messenger_id = ''"
          />
          <UiInput
            v-model="profileInfo.messenger_id"
            :error="errors.messenger_id"
            :disabled="!profileInfo.messenger"
            :placeholder="messengerPlaceholder"
            :label="$t(`team_popup.messenger_id_label`)"
            size="big"
            autosize
          />
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          @click="handleSave"
        >
          {{ $t('crm.buttons.save') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
